import { action, computed, observable } from 'mobx';
import { StickyConfig } from '../models/configuration/StickyConfig';
import { Stores } from '../models/Stores';
import { BreakpointSize, Operator } from '../types';
import { compareValues } from '../utils/compareValues';
import { BaseStore, DataStore } from './';

export class ScreenSizeStore extends BaseStore {
    @observable
    public breakpointSize: BreakpointSize;

    @computed
    public get sm(): number {
        const width = this.dataStore?.theme?.breakpoints?.sm;
        if (width) {
            const size = this.convertSizeToNumber(width);
            return size;
        }
        return 640;
    }

    @computed
    public get md(): number {
        const width = this.dataStore?.theme?.breakpoints?.md;
        if (width) {
            const size = this.convertSizeToNumber(width);
            return size;
        }
        return 768;
    }

    @computed
    public get lg(): number {
        const width = this.dataStore?.theme?.breakpoints?.lg;
        if (width) {
            const size = this.convertSizeToNumber(width);
            return size;
        }
        return 1024;
    }

    @computed
    get isMobile(): boolean {
        switch (this.breakpointSize) {
            case 'sm':
            case 'md':
                return true;
            case 'lg':
            default:
                return false;
        }
    }

    @computed
    get stickyConfig(): StickyConfig | undefined {
        const { stickyConfig } = this.dataStore?.config;

        if (stickyConfig) {
            return stickyConfig[this.breakpointSize];
        }
    }

    @computed
    private get dataStore(): DataStore | undefined {
        return this.stores.data;
    }

    private timeout: NodeJS.Timeout;

    constructor(stores: Stores) {
        super(stores);
    }

    public init() {
        setTimeout(() => {
            window.addEventListener('resize', () => this.handleResize());
        }, 500);
        this.handleResize(0);
    }

    public compareBreakpoints(
        leftBreakpoint: BreakpointSize,
        operator: Operator,
        rightBreakpoint: BreakpointSize
    ) {
        const breakpointList: Array<BreakpointSize> = ['sm', 'md', 'lg'];
        const leftIndex = breakpointList.indexOf(leftBreakpoint);
        const rightIndex = breakpointList.indexOf(rightBreakpoint);
        return compareValues(leftIndex, operator, rightIndex, false);
    }

    private get currentWidth() {
        return window.innerWidth;
    }

    /**
     * Also known as mobile
     *
     * @private
     * @returns
     */
    private isBreakpointSm() {
        return compareValues(this.currentWidth, '<=', this.sm, false);
    }

    /**
     * Also known as Tablet
     *
     * @private
     * @returns
     */
    private isBreakpointMd() {
        return compareValues(this.currentWidth, '<=', this.md, false);
    }

    /**
     * Also known as Desktop
     *
     * @private
     * @returns
     */
    private isBreakpointLg() {
        return compareValues(this.currentWidth, '>', this.md, false);
    }

    private convertSizeToNumber(size: string) {
        let strNum;
        let multiplyToPx = false;
        const sizeTypes = ['px', 'em', 'rem'];
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < sizeTypes.length; i++) {
            const type = sizeTypes[i];
            if (size.indexOf(type) > 0) {
                if (type !== 'px') {
                    multiplyToPx = true;
                }
                strNum = size.replace(type, '');
                break;
            }
        }
        const num = parseInt(strNum, 10);
        return multiplyToPx ? num * 16 : num;
    }

    @action
    private handleResize = (timeout: number = 500) => {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
        this.timeout = setTimeout(() => {
            this.timeout = null;
            if (this.isBreakpointSm()) {
                this.breakpointSize = 'sm';
            } else if (this.isBreakpointMd()) {
                this.breakpointSize = 'md';
            } else if (this.isBreakpointLg()) {
                this.breakpointSize = 'lg';
            } else {
                this.breakpointSize = 'lg';
            }
        }, timeout);
    };
}



// WEBPACK FOOTER //
// ./src/stores/ScreenSizeStore.ts